import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { DeskPackageSpec, DeskPcSpec } from "../scenario/types";

const DEG = Math.PI / 180;
/** Must match DeskWorkstation's own DESK_H so the box rests on the same desk top. */
const DESK_H = 0.74;

/** Screen-space pin (percent of viewport) for the clickable sticky note. */
export type PackagePin = { xPct: number; yPct: number; visible: boolean; distance: number };

/**
 * The square yellow Post-it stuck on the box lid — a real handwritten-note
 * look (warm paper gradient + speckle grain + a peeled bottom-right corner +
 * a soft all-round lift vignette so it reads as an actual sticky note, not a
 * flat yellow decal) with a multi-line message about the USB stick inside
 * needing to be opened, plus a scrawled-but-illegible signature squiggle
 * beneath it (2026-07-26: previously just said "Click to open" with no
 * paper texture or in-world reason to open the box). The signature is
 * deliberately NOT a readable name/initials — the decision card that opens
 * on click (`usb_decision`'s `senderLine`) explicitly plays "left at your
 * desk, no name" as the red flag, so the close-up note must back that up
 * rather than contradict it with a legible signature.
 */
function makeStickyTexture(): THREE.CanvasTexture {
  const s = 512;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s;
  const g = cv.getContext("2d")!;

  // Warm paper gradient, deepening slightly toward the bottom-right as if
  // catching a soft ambient shadow.
  const paper = g.createLinearGradient(0, 0, s, s);
  paper.addColorStop(0, "#fff3a0");
  paper.addColorStop(0.55, "#ffe873");
  paper.addColorStop(1, "#f3d24c");
  g.fillStyle = paper;
  g.fillRect(0, 0, s, s);

  // Subtle paper-fiber speckle grain.
  g.globalAlpha = 0.05;
  for (let i = 0; i < 260; i++) {
    g.fillStyle = Math.random() > 0.5 ? "#000000" : "#ffffff";
    g.fillRect(Math.random() * s, Math.random() * s, 1.4, 1.4);
  }
  g.globalAlpha = 1;

  // Darker band along the top edge, like the strip stuck to the box.
  const topShadow = g.createLinearGradient(0, 0, 0, 40);
  topShadow.addColorStop(0, "rgba(0,0,0,0.14)");
  topShadow.addColorStop(1, "rgba(0,0,0,0)");
  g.fillStyle = topShadow;
  g.fillRect(0, 0, s, 40);

  // Peeled bottom-right corner fold — the classic Post-it tell.
  const fold = 48;
  g.save();
  g.beginPath();
  g.moveTo(s - fold, s);
  g.lineTo(s, s);
  g.lineTo(s, s - fold);
  g.closePath();
  const foldShade = g.createLinearGradient(s - fold, s, s, s - fold);
  foldShade.addColorStop(0, "rgba(0,0,0,0.2)");
  foldShade.addColorStop(1, "rgba(0,0,0,0.02)");
  g.fillStyle = foldShade;
  g.fill();
  g.restore();

  // Soft all-round vignette so every edge reads as slightly lifted paper.
  const vignette = g.createRadialGradient(s / 2, s / 2, s * 0.32, s / 2, s / 2, s * 0.72);
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(1, "rgba(0,0,0,0.1)");
  g.fillStyle = vignette;
  g.fillRect(0, 0, s, s);

  // Handwritten message.
  g.textAlign = "center";
  g.fillStyle = "#3a2e0c";
  g.font = "700 56px 'Segoe Script', 'Comic Sans MS', cursive";
  g.fillText("USB stick", s / 2, s * 0.33);
  g.fillText("inside \u2014", s / 2, s * 0.44);
  g.font = "700 44px 'Segoe Script', 'Comic Sans MS', cursive";
  g.fillStyle = "#5a3a0a";
  g.fillText("please load", s / 2, s * 0.58);
  g.fillText("before 2pm!", s / 2, s * 0.685);

  // Illegible scrawled "signature" — deliberately not a readable name (see
  // block comment above): a fast, looping single-stroke squiggle that reads
  // as "someone signed this in a hurry" without identifying anyone, plus a
  // short flourish underline.
  g.strokeStyle = "rgba(44,36,8,0.55)";
  g.lineCap = "round";
  g.lineJoin = "round";
  g.lineWidth = 3.2;
  g.beginPath();
  g.moveTo(s * 0.56, s * 0.87);
  g.bezierCurveTo(s * 0.6, s * 0.83, s * 0.63, s * 0.91, s * 0.67, s * 0.86);
  g.bezierCurveTo(s * 0.7, s * 0.82, s * 0.72, s * 0.9, s * 0.76, s * 0.865);
  g.bezierCurveTo(s * 0.785, s * 0.845, s * 0.8, s * 0.87, s * 0.83, s * 0.855);
  g.stroke();
  g.lineWidth = 1.8;
  g.beginPath();
  g.moveTo(s * 0.55, s * 0.905);
  g.bezierCurveTo(s * 0.64, s * 0.885, s * 0.74, s * 0.93, s * 0.84, s * 0.9);
  g.stroke();

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Shipping label glued to the box top (address-label look). */
function makeLabelTexture(labelText: string): THREE.CanvasTexture {
  const w = 512;
  const h = 288;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#f4efe2";
  g.fillRect(0, 0, w, h);
  g.strokeStyle = "rgba(0,0,0,0.18)";
  g.lineWidth = 3;
  g.strokeRect(6, 6, w - 12, h - 12);
  g.fillStyle = "#1a1a1a";
  g.font = "700 30px Segoe UI, Arial, sans-serif";
  g.textAlign = "left";
  g.fillText("PRIORITY", 26, 52);
  g.fillStyle = "rgba(0,0,0,0.75)";
  g.font = "22px Segoe UI, Arial, sans-serif";
  wrapText(g, labelText, 26, 96, w - 52, 30);
  // barcode-ish stripes
  g.fillStyle = "#151515";
  let x = 26;
  for (let i = 0; i < 34; i++) {
    const bw = 3 + (i % 3);
    g.fillRect(x, h - 58, bw, 40);
    x += bw + 4;
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function wrapText(g: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  const words = text.split(" ");
  let line = "";
  let cy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (g.measureText(test).width > maxWidth && line) {
      g.fillText(line, x, cy);
      line = word;
      cy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) g.fillText(line, x, cy);
}

/** Live tuning override for desk-relative offset: `?pkgOff=x,y,z`. */
function readOffsetOverride(spec: DeskPackageSpec): [number, number, number] {
  let offset = spec.offset ?? [-0.46, 0.055, 0.02];
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("pkgOff");
    if (q) {
      const parts = q.split(",").map(Number);
      if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
        offset = [parts[0], parts[1], parts[2]];
      }
    }
  }
  return offset;
}

/**
 * A cardboard package sitting on the same desk as `DeskWorkstation`, clear of
 * the monitor/keyboard. Carries a shipping label + a yellow "Click to open"
 * sticky note propped against the lid. Projects the sticky note's world
 * position to screen % each frame via `onPin`, mirroring DeskWorkstation's
 * own sticky-note hotspot pattern, so App can render a clickable DOM hotspot
 * glued to it. Stays on the desk (never disappears) — App swaps to the full
 * UsbDevice overlay once the learner clicks the hotspot.
 */
export function DeskPackage({
  deskSpec,
  spec,
  onPin,
}: {
  deskSpec: DeskPcSpec;
  spec: DeskPackageSpec;
  onPin: (pin: PackagePin) => void;
}) {
  const { camera } = useThree();
  const noteAnchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<PackagePin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const scale = deskSpec.scale ?? 1;
  const yaw = (deskSpec.rotationDeg ?? 0) * DEG;
  const offset = useMemo(() => readOffsetOverride(spec), [spec]);

  const labelTex = useMemo(() => makeLabelTexture(spec.labelText ?? "Handle with care"), [spec.labelText]);
  const stickyTex = useMemo(() => makeStickyTexture(), []);

  useFrame(() => {
    const anchor = noteAnchor.current;
    if (!anchor) return;
    anchor.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: PackagePin = { xPct, yPct, visible: inFront && onScreen && distance < 6, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __packagePin?: PackagePin }).__packagePin = pin;
    }
  });

  const boxW = 0.34;
  const boxH = 0.22;
  const boxD = 0.26;

  return (
    <group position={deskSpec.position} rotation={[0, yaw, 0]} scale={scale}>
      <group position={[offset[0], DESK_H + boxH / 2 + offset[1], offset[2]]}>
        {/* Cardboard box */}
        <mesh>
          <boxGeometry args={[boxW, boxH, boxD]} />
          <meshStandardMaterial color="#c8a06a" roughness={0.85} metalness={0.02} />
        </mesh>
        {/* Packing tape strip, top */}
        <mesh position={[0, boxH / 2 + 0.001, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.05, boxD + 0.002]} />
          <meshStandardMaterial color="#e7dcc0" roughness={0.5} transparent opacity={0.85} />
        </mesh>
        {/* Shipping label, front face */}
        <mesh position={[0, 0.01, boxD / 2 + 0.001]}>
          <planeGeometry args={[boxW * 0.72, boxH * 0.5]} />
          <meshStandardMaterial map={labelTex} roughness={0.9} />
        </mesh>

        {/* Sticky note, propped against the box's front-right corner, angled
            just enough to read as "leaning" without foreshortening it into a
            thin sliver from the desk's establishing/close-up camera angles.
            Bumped 0.1 -> 0.16 (60% bigger) so the USB message + signature
            are actually legible up close, and the Y-rotation cut from 0.55
            (~31°, which turned a genuinely square plane into a near-edge-on
            view from most angles the learner sees it from) down to 0.16 rad
            (~9°) — still visibly propped, but reads as the real square note
            it is (2026-07-26 regression report: "not square, no USB
            reference visible"). */}
        <group ref={noteAnchor} position={[boxW / 2 + 0.02, boxH * 0.24, boxD * 0.06]} rotation={[-0.06, 0.16, -0.03]}>
          <mesh>
            <planeGeometry args={[0.16, 0.16]} />
            <meshStandardMaterial map={stickyTex} roughness={0.9} side={THREE.DoubleSide} />
          </mesh>
        </group>
      </group>
    </group>
  );
}
